import { Clock, Trash2, ExternalLink, Shield, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CheckResult } from "./UrlCheckerResult";

interface CacheEntry {
  result: CheckResult;
  timestamp: number;
  domain: string;
}

interface CacheListProps {
  cachedUrls: CacheEntry[];
  onClearEntry: (url: string) => void;
  onClearAll: () => void;
  onSelectUrl: (url: string) => void;
  getTimeRemaining: (timestamp: number) => string;
}

export function CacheList({
  cachedUrls,
  onClearEntry,
  onClearAll,
  onSelectUrl,
  getTimeRemaining,
}: CacheListProps) {
  if (cachedUrls.length === 0) {
    return null;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "safe":
        return <Shield className="h-4 w-4 text-safe" />;
      case "caution":
        return <AlertTriangle className="h-4 w-4 text-caution" />;
      case "risk":
        return <XCircle className="h-4 w-4 text-risk" />;
      default:
        return <Shield className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBgClass = (status: string) => {
    switch (status) {
      case "safe":
        return "bg-safe/10 border-safe/20";
      case "caution":
        return "bg-caution/10 border-caution/20";
      case "risk":
        return "bg-risk/10 border-risk/20";
      default:
        return "bg-muted border-border";
    }
  };

  return (
    <div className="mt-8 p-4 bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">
            Recently Checked ({cachedUrls.length})
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-muted-foreground hover:text-risk"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Clear All
        </Button>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Results are cached for 24 hours for faster access
      </p>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {cachedUrls.map((entry, index) => (
          <div
            key={`${entry.domain}-${index}`}
            className={`flex items-center justify-between p-3 rounded-md border ${getStatusBgClass(entry.result.status)}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {getStatusIcon(entry.result.status)}
              <button
                onClick={() => onSelectUrl(`https://${entry.domain}`)}
                className="text-sm font-medium text-foreground hover:text-primary truncate text-left"
              >
                {entry.domain}
              </button>
            </div>
            
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Expires in {getTimeRemaining(entry.timestamp)}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onSelectUrl(`https://${entry.domain}`)}
                  title="View result"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 hover:text-risk"
                  onClick={() => onClearEntry(`https://${entry.domain}`)}
                  title="Remove from cache"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
